import { Head } from 'vite-react-ssg';
import { Link } from 'react-router-dom';
import { pageTitle } from '@mockd/shared/brand';
import { ROUTES } from '../routes';

const LAST_UPDATED = 'October 16, 2026';

export function Privacy() {
    return (
        <div className="min-h-screen bg-base-200">
            <Head>
                <title>{pageTitle('Privacy Policy')}</title>
                <meta name="description" content="How mockd collects, stores, and uses your data." />
                <meta property="og:title" content={pageTitle('Privacy Policy')} />
                <meta property="og:description" content="How mockd collects, stores, and uses your data." />
                <meta property="og:url" content="https://mockd.sh/privacy" />
            </Head>
            <div className="max-w-3xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h1 className="tracking-tight">Privacy Policy</h1>
                <p className="mt-2 text-sm text-base-content/60">Last updated: {LAST_UPDATED}</p>

                <div className="mt-10 space-y-8 text-base-content/80 leading-relaxed">
                    <section>
                        <p>
                            mockd ("we", "us", "our") operates mockd.sh, a mock API server platform.
                            This Privacy Policy explains what data we collect, why we collect it, and
                            how it's stored when you use our service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">1. Information We Collect</h2>
                        <h3 className="font-medium text-base-content mt-4 mb-1">Account information</h3>
                        <p>
                            When you create an account, we collect your email address and, if you sign
                            in with GitHub, basic profile information (name, avatar, email) provided by
                            GitHub OAuth. If you register with a password, it is stored using a salted
                            hash and is never stored or transmitted in plain text. If you sign in via a
                            magic link, we generate a single-use, time-limited token that is not usable
                            after it expires or is redeemed.
                        </p>
                        <h3 className="font-medium text-base-content mt-4 mb-1">Project and endpoint data</h3>
                        <p>
                            We store the projects, mock endpoints, rules, and response templates you
                            create in order to operate the service. This is necessary to provide the
                            core functionality you sign up for.
                        </p>
                        <h3 className="font-medium text-base-content mt-4 mb-1">Request logs</h3>
                        <p>
                            When a mock endpoint you create receives a request, we log details of that
                            request (method, path, headers, query parameters, and body) so you can
                            inspect and replay traffic. Retention of these logs depends on your plan
                            (see our <Link to={ROUTES.pricing()} className="link link-primary">Pricing</Link> page)
                            and logs are automatically deleted after the retention period. Note that
                            request logs may contain data sent to your mock endpoints by third parties
                            or your own systems — you are responsible for not sending sensitive personal
                            data through mock endpoints you control.
                        </p>
                        <h3 className="font-medium text-base-content mt-4 mb-1">Usage and technical data</h3>
                        <p>
                            Cloudflare, our infrastructure provider, collects standard technical data
                            (IP address, request metadata) as part of operating our Workers and network
                            security. We do not currently run separate third-party analytics or error
                            tracking on the dashboard; if that changes, we will update this policy and,
                            where legally required, ask for your consent first.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">2. Cookies</h2>
                        <p>
                            We use a small number of cookies, scoped to the <code className="text-sm">.mockd.sh</code> domain,
                            to keep you signed in and to protect against cross-site request forgery.
                            These are essential, first-party cookies required for the service to
                            function — we do not use third-party advertising or tracking cookies.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">3. How We Use Your Information</h2>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li>To provide, maintain, and operate the mockd service</li>
                            <li>To authenticate you and keep your account secure</li>
                            <li>To send transactional email (e.g. magic links, verification, account notices) via Resend</li>
                            <li>To enforce plan limits and prevent abuse of the service</li>
                            <li>To respond to support requests you send us</li>
                        </ul>
                        <p className="mt-3">We do not sell your personal information.</p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">4. Third-Party Services</h2>
                        <p>We rely on a small number of infrastructure providers to operate mockd:</p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li><span className="font-medium text-base-content">Cloudflare</span> — hosting, Workers runtime, D1 database, and Durable Objects storage</li>
                            <li><span className="font-medium text-base-content">Resend</span> — delivery of transactional email</li>
                            <li><span className="font-medium text-base-content">GitHub</span> — OAuth sign-in, if you choose to use it</li>
                        </ul>
                        {/* TODO (launch week): add Stripe here once billing is integrated, e.g.:
                            <li><span className="font-medium text-base-content">Stripe</span> — payment processing for paid plans. Stripe receives your payment details directly; we never see or store your full card number.</li> */}
                        <p className="mt-3">
                            Each provider processes data only as needed to deliver the functionality
                            above and under their own privacy and security commitments.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">5. Security</h2>
                        <p>
                            All data is encrypted in transit (TLS) and at rest by our infrastructure
                            provider. No method of transmission or storage is completely secure, but we
                            take reasonable measures to protect your data and limit access to it.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">6. Data Retention and Deletion</h2>
                        <p>
                            We retain account and project data for as long as your account is active.
                            Request logs are retained according to your plan's retention period and
                            deleted automatically afterward.
                        </p>
                        <p className="mt-3">
                            You can delete your account at any time from your Account settings. Deletion
                            is immediate: we delete your account, your personal projects with their
                            endpoints, rules, and logs, and your account-level settings, except where
                            we're required to retain records for legal, billing, or security purposes.
                            Team projects you created belong to the team, so they stay with it and pass
                            to the team's owner. Deleted data may persist in encrypted backups for up to
                            30 days after deletion before being purged.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">7. Your Rights</h2>
                        <p>
                            You can access, update, or delete most of your account data, or delete your
                            account entirely, directly from
                            your <Link to={ROUTES.account()} className="link link-primary">Account</Link> settings.
                            For anything else — including a full data export — contact us at{' '}
                            <a href="mailto:support@mockd.sh" className="link link-primary">support@mockd.sh</a>.
                        </p>
                        {/* TODO (launch week): add a short GDPR/CCPA paragraph enumerating access, rectification,
                            erasure, portability, restriction, objection, and the right to lodge a complaint with a
                            supervisory authority; state legal bases (contract performance, legitimate interest for
                            abuse prevention); and add one sentence on international transfers (Cloudflare/Resend DPAs,
                            SCCs / Data Privacy Framework). */}
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">8. Children</h2>
                        <p>
                            mockd is not directed at children, and you must be at least 16 years old to
                            use the Service. We do not knowingly collect personal information from anyone
                            under 16. If you believe a child under 16 has created an account, contact us
                            at{' '}
                            <a href="mailto:support@mockd.sh" className="link link-primary">support@mockd.sh</a>{' '}
                            and we will delete it.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">9. Changes to This Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time. If we make material
                            changes, we'll update the "Last updated" date above and notify you by email
                            before the changes take effect.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-base-content mb-2">10. Contact</h2>
                        <p>
                            Questions about this policy? Email{' '}
                            <a href="mailto:support@mockd.sh" className="link link-primary">support@mockd.sh</a>.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
}
